import { ArrowRight } from "lucide-react";
import type { Hsb, RoundColor } from "../color";
import { feedbackLabel, hsbToHex } from "../color";
import { copy } from "../copy";

export type ReviewData = {
  target: RoundColor;
  guess: Hsb;
  score: number;
  distance: number;
};

export function ReviewStage({
  review,
  roundIndex,
  roundCount,
  onNext,
}: {
  review: ReviewData;
  roundIndex: number;
  roundCount: number;
  onNext: () => void;
}) {
  const isLast = roundIndex === roundCount - 1;
  const label = feedbackLabel(review.distance);
  const guessHex = hsbToHex(review.guess);
  return (
    <article className="stage review-stage" aria-label={copy.reviewTitle}>
      <header className="review-head">
        <span className="pill">
          {roundIndex + 1} / {roundCount} {copy.round}
        </span>
        <h2>{copy.reviewTitle}</h2>
      </header>
      <div className="review-grid">
        <ReviewCard
          variant="target"
          title={copy.target}
          color={review.target.hex}
          hsbLabel={copy.targetHsb}
          hsb={review.target}
        />
        <ReviewCard
          variant="guess"
          title={copy.guess}
          color={guessHex}
          hsbLabel={copy.guessHsb}
          hsb={review.guess}
        />
      </div>
      <div className="review-summary">
        <div className="review-delta">
          <small>{copy.deltaLabel}</small>
          <strong>{review.distance.toFixed(2)}</strong>
        </div>
        <p className="review-feedback" role="status" aria-live="polite">
          {label}
        </p>
        <div className="review-score">
          <small>{copy.scoreLabelKo}</small>
          <strong>
            {review.score}
            <em>{copy.point}</em>
          </strong>
        </div>
      </div>
      <button className="primary review-next" onClick={onNext}>
        {isLast ? copy.viewResults : copy.nextRound}
        <ArrowRight size={18} />
      </button>
    </article>
  );
}

function ReviewCard({
  variant,
  title,
  color,
  hsbLabel,
  hsb,
}: {
  variant: "target" | "guess";
  title: string;
  color: string;
  hsbLabel: string;
  hsb: Hsb;
}) {
  return (
    <section
      className={`color-card review-card ${variant}`}
      style={{ background: color }}
      aria-label={title}
    >
      <div className="card-overlay">
        <span className="pill ghost">{title}</span>
        <p className="hsb-readout">
          <small>{hsbLabel}</small>
          H {hsb.hue} · S {hsb.saturation} · B {hsb.brightness}
        </p>
      </div>
    </section>
  );
}
